/**
 * MissionSection Component
 *
 * Displays organizational mission statement with leadership preview
 * Includes link to full leadership section
 */

import React from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
} from '@mui/material'
import { MissionContent } from '@/data/types'
import markLudwigImg from '@/assets/mark-ludwig.jpg'

export interface MissionSectionProps {
  /**
   * Mission content (title, mission text, leadership preview)
   */
  content: MissionContent

  /**
   * Callback when user clicks "Learn More About Our Leadership"
   */
  onLearnMoreClick?: () => void

  /**
   * Optional CSS class name for styling
   */
  className?: string

  /**
   * Test ID for automated testing
   * @default "mission-section"
   */
  testId?: string
}

export const MissionSection: React.FC<MissionSectionProps> = ({
  content,
  onLearnMoreClick,
  className,
  testId = 'mission-section',
}) => {
  const missionHeadingId = 'mission-heading'

  const handleLearnMore = () => {
    if (onLearnMoreClick) {
      onLearnMoreClick()
    } else {
      // Default behavior: scroll to leadership section
      const leadershipSection = document.getElementById('leadership')
      if (leadershipSection) {
        leadershipSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }

  return (
    <Box
      component="section"
      role="region"
      aria-labelledby={missionHeadingId}
      data-testid={testId}
      className={className}
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          {/* Mission Content */}
          <Grid item xs={12} md={7}>
            <Typography
              id={missionHeadingId}
              variant="h2"
              component="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 700,
                mb: 3,
                color: 'text.primary',
              }}
            >
              {content.title}
            </Typography>

            <Typography
              variant="body1"
              component="p"
              sx={{
                fontSize: { xs: '1.125rem', md: '1.25rem' },
                lineHeight: 1.8,
                color: 'text.secondary',
                mb: 0,
              }}
            >
              {content.content}
            </Typography>
          </Grid>

          {/* Leadership Preview Card */}
          <Grid item xs={12} md={5}>
            <Card
              elevation={3}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 8,
                },
              }}
            >
              <CardMedia
                component="picture"
                sx={{
                  height: 280,
                  position: 'relative',
                }}
              >
                <Box
                  component="img"
                  src={markLudwigImg}
                  alt={content.leadershipPreview.photoAlt}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </CardMedia>

              <CardContent
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  p: 3,
                }}
              >
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                    color: 'text.primary',
                  }}
                >
                  Meet Our {content.leadershipPreview.title.split(' & ').pop()}
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    mb: 2,
                  }}
                >
                  {content.leadershipPreview.name}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.7,
                    mb: 3,
                    flexGrow: 1,
                  }}
                >
                  {content.leadershipPreview.bioTeaser}
                </Typography>

                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleLearnMore}
                  aria-label={`Learn more about ${content.leadershipPreview.name}`}
                  sx={{
                    mt: 'auto',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Learn More
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}
